import { useState } from 'react';
import { useMutation } from 'react-apollo';

import {
  cardCreate as cardCreateMutation,
  cardUpdate as cardUpdateMutation,
} from '../../../graphql/mutations/card';
import {
  cardSideCreate as cardSideCreateMutation,
  cardSideUpdate as cardSideUpdateMutation,
} from '../../../graphql/mutations/cardSide';
import { cardElementConfigurationCreate as cardElementConfigurationCreateMutation } from '../../../graphql/mutations/cardElementConfiguration';
import {
  cardSideGroupCreate as cardSideGroupCreateMutation,
  cardSideGroupUpdate as cardSideGroupUpdateMutation,
} from '../../../graphql/mutations/cardSideGroup';
import { getErrorString } from '../../../../../util/normalize-errors';
import {
  getConfigurableElementsForCardSide,
  mapElementStateToElementConfigurationInput,
} from './helpers';
import { cardGroupUpdate as cardGroupUpdateMutation } from '../../../graphql/mutations/cardGroup';

/**
 * Routine handling the Card creation process.
 *
 * @param {Object} options
 * @param {Array<Object>} options.selectedCardSideGroups The selected CardSideGroups for the Card (containing all the elements etc).
 * @param {Array<Object>} options.cardElementConfigurationStates The CardElement configuration states.
 * @param {Array<Object>} options.cardTemplates The CardTemplates.
 * @param {Function} options.onError Error callback.
 * @param {Function} options.onSuccess Success callback.
 * @param {Object} options.selectedCardGroup The selected CardGroup.
 * @param {Array} options.selectedCategories The selected CardCategories.
 * @returns {{onCreate: (function(): Promise<undefined>), loading: boolean, error: string}}
 */
const useCardCreateRoutine = ({
  selectedCardSideGroups,
  selectedCardGroup,
  selectedCategories,
  cardElementConfigurationStates,
  cardTemplates,
  onError,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardCreate] = useMutation(cardCreateMutation);
  const [cardUpdate] = useMutation(cardUpdateMutation);
  const [cardSideGroupCreate] = useMutation(cardSideGroupCreateMutation);
  const [cardSideGroupUpdate] = useMutation(cardSideGroupUpdateMutation);
  const [cardSideCreate] = useMutation(cardSideCreateMutation);
  const [cardSideUpdate] = useMutation(cardSideUpdateMutation);
  const [cardGroupUpdate] = useMutation(cardGroupUpdateMutation);
  const [cardElementConfigurationCreate] = useMutation(
    cardElementConfigurationCreateMutation,
  );

  const onCreate = async () => {
    try {
      if (loading) {
        return;
      }

      if (!selectedCardGroup) {
        setError('Please select a CardGroup.');
        return;
      }

      setLoading(true);
      setError('');

      // 1a. Create the Card that will hold everything.
      const cardResponse = await cardCreate({
        variables: {
          input: {},
        },
      });
      const cardId = cardResponse.data.cardCreate.card.id;

      // 2a. Update the Card with the CardCategories
      // TODO [cards] Add card_category_ids in cardCreate
      await cardUpdate({
        variables: {
          input: {
            card_id: cardId,
            card_category_ids: selectedCategories.map(x => x.id),
          },
        },
      });

      // 2. Loop over all CardSideGroups that contain selected CardTemplates.
      const pendingCardSideGroups = selectedCardSideGroups.filter(
        x => x.card_templates.length > 0,
      );

      for (const cardSideGroup of pendingCardSideGroups) {
        const cardSides = [];

        // 3. Create the CardSideGroup and link it to this Card and side.
        const cardSideGroupCreateResponse = await cardSideGroupCreate({
          variables: {
            input: {
              card_id: cardId,
              side: cardSideGroup.side,
              asset_image_id: cardSideGroup.asset_image_id || undefined,
              name: cardSideGroup.name,
            },
          },
        });
        const cardSideGroupId =
          cardSideGroupCreateResponse.data.cardSideGroupCreate.card_side_group
            .id;

        // 4. For every CardTemplate in this CardSideGroup create its CardSide
        for (const stateCardTemplate of cardSideGroup.card_templates) {
          const cardTemplate = cardTemplates.find(
            x => x.id === stateCardTemplate.id,
          );
          const cardSideCreateResponse = await cardSideCreate({
            variables: {
              input: {
                card_template_id: cardTemplate.id,
                name: cardTemplate.name,
                asset_image_id: stateCardTemplate.asset_image_id || undefined,
              },
            },
          });
          const cardSideId =
            cardSideCreateResponse.data.cardSideCreate.card_side.id;

          // 4b. Assign the selected CardCategories to the CardSide
          // TODO [cards] Add card_category_ids in cardSideCreate
          await cardSideUpdate({
            variables: {
              input: {
                card_side_id: cardSideId,
                card_category_ids: selectedCategories.map(x => x.id),
              },
            },
          });

          cardSides.push({
            card_side_id: cardSideId,
            // TODO [cards] support display_order
          });

          const cardTemplateElementConfigurations =
            getConfigurableElementsForCardSide(
              cardTemplate,
              cardElementConfigurationStates,
            );

          // 5. For each configurable element that corresponds to this CardSide
          // create their CardElementConfiguration.
          for (const cardTemplateElementConfiguration of cardTemplateElementConfigurations) {
            await cardElementConfigurationCreate({
              variables: {
                input: {
                  card_element_id: cardTemplateElementConfiguration.element.id,
                  card_side_id: cardSideId,
                  ...mapElementStateToElementConfigurationInput(
                    cardTemplateElementConfiguration,
                  ),
                },
              },
            });
          }
        }

        // 6. Update the above CardSideGroup to reference the CardSides we created.
        await cardSideGroupUpdate({
          variables: {
            input: {
              card_side_group_id: cardSideGroupId,
              card_sides: cardSides,
            },
          },
        });
      }

      // 7. Add the final Card to the CardGroup
      await cardGroupUpdate({
        variables: {
          input: {
            card_group_id: selectedCardGroup.id,
            add_cards: [{ card_id: cardId }],
          },
        },
      });

      onSuccess?.(cardResponse.data.cardCreate.card);
    } catch (responseError) {
      setError(getErrorString(responseError));
      onError?.(responseError);
    } finally {
      setLoading(false);
    }
  };

  return {
    onCreate,
    loading,
    error,
  };
};

export default useCardCreateRoutine;
